/**
 * Панель кластеров услуг: поиск, фильтр и управление составом кластеров.
 * Дата: 16.03.2026
 */
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Modal, Pagination, SearchInput, Select } from '@/components/ui';
import { ClusterCard } from './ClusterCard';
import { ClusterServicesShell } from './ClusterServicesShell';
import {
  ServiceCluster,
  ServiceClusterMember,
  CreateClusterRequest,
  ClusterPreset,
  PreviewPresetsResponse,
} from '@/api/service-clusters';
import { MedicalService } from '@/api/medicalServices';
import { Plus, RefreshCw } from 'lucide-react';
import {
  useApplyServiceClusterPresets,
  useCreateCluster,
  useDeleteCluster,
  usePreviewServiceClusterPresets,
  useServiceClusterPresets,
  useSyncServiceClusters,
  useUpdateCluster,
} from '@/hooks/useServiceClusters';
import { CLUSTER_ICON_OPTIONS, DEFAULT_CLUSTER_COLORS } from './clusterVisuals';

interface ClusterPanelProps {
  clusters: ServiceCluster[];
  servicesByCluster: Record<string, ServiceClusterMember[]>;
  draggedService: MedicalService | null;
  onDrop: (clusterId: string, serviceId: string) => Promise<void>;
  onRemoveService: (clusterId: string, serviceId: string) => Promise<void>;
  onReorderServices: (clusterId: string, services: ServiceClusterMember[]) => Promise<void>;
  selectedClusterId?: string;
  onSelectCluster?: (clusterId: string | null) => void;
  isLoading?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
}

const CLUSTERS_PAGE_SIZE = 6;

export function ClusterPanel({
  clusters,
  servicesByCluster,
  draggedService,
  onDrop,
  onRemoveService,
  onReorderServices,
  selectedClusterId,
  onSelectCluster,
  isLoading,
  loadError,
  onRetry,
  isRetrying,
  className,
}: ClusterPanelProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPresetsModalOpen, setIsPresetsModalOpen] = useState(false);
  const [editingCluster, setEditingCluster] = useState<ServiceCluster | null>(null);
  const [clusterSearch, setClusterSearch] = useState('');
  const [clusterOffset, setClusterOffset] = useState(0);
  const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);
  const [servicesShellCluster, setServicesShellCluster] = useState<ServiceCluster | null>(null);
  const [selectedPresetKeys, setSelectedPresetKeys] = useState<string[]>([]);
  const [presetPreview, setPresetPreview] = useState<PreviewPresetsResponse | null>(null);
  
  // Форма создания/редактирования
  const [formData, setFormData] = useState<CreateClusterRequest>({
    name: '',
    description: '',
    color: DEFAULT_CLUSTER_COLORS[0].value,
    icon: CLUSTER_ICON_OPTIONS[0].value,
    display_order: 0,
    is_active: true,
  });

  const createCluster = useCreateCluster();
  const updateCluster = useUpdateCluster();
  const deleteCluster = useDeleteCluster();
  const presetsQuery = useServiceClusterPresets();
  const previewPresets = usePreviewServiceClusterPresets();
  const applyPresets = useApplyServiceClusterPresets();
  const syncClusters = useSyncServiceClusters();

  const presets = presetsQuery.data?.presets || [];

  const handleCreate = () => {
    createCluster.mutate(formData, {
      onSuccess: () => {
        setIsCreateModalOpen(false);
        setFormData({
          name: '',
          description: '',
          color: DEFAULT_CLUSTER_COLORS[0].value,
          icon: CLUSTER_ICON_OPTIONS[0].value,
          display_order: 0,
          is_active: true,
        });
      },
    });
  };

  const handleEdit = (cluster: ServiceCluster) => {
    setEditingCluster(cluster);
    setFormData({
      name: cluster.name,
      description: cluster.description || '',
      color: cluster.color || DEFAULT_CLUSTER_COLORS[0].value,
      icon: cluster.icon || CLUSTER_ICON_OPTIONS[0].value,
      display_order: cluster.display_order,
      is_active: cluster.is_active,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!editingCluster) return;
    
    updateCluster.mutate(
      {
        id: editingCluster.id,
        data: {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          icon: formData.icon,
          display_order: formData.display_order,
          is_active: formData.is_active,
        },
      },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          setEditingCluster(null);
        },
      }
    );
  };

  const handleDelete = (clusterId: string) => {
    deleteCluster.mutate(clusterId);
  };

  const handleToggleActive = (cluster: ServiceCluster) => {
    const nextIsActive = !cluster.is_active;
    const warning = nextIsActive
      ? `Активировать кластер "${cluster.name}"?`
      : `Деактивировать кластер "${cluster.name}"? Рекомендации по нему больше не будут уходить.`;

    if (!window.confirm(warning)) {
      return;
    }

    updateCluster.mutate(
      {
        id: cluster.id,
        data: { is_active: nextIsActive },
      },
      {
        onSuccess: () => {
          if (!nextIsActive && selectedClusterId === cluster.id) {
            onSelectCluster?.(null);
          }
        },
      }
    );
  };

  const handleSyncClusters = () => {
    syncClusters.mutate({ full_sync: true, limit: 100 });
  };

  const sortedClusters = useMemo(
    () =>
      [...clusters].sort((a, b) => {
        if (a.is_active !== b.is_active) {
          return Number(b.is_active) - Number(a.is_active);
        }
        if (a.display_order !== b.display_order) {
          return a.display_order - b.display_order;
        }
        return a.name.localeCompare(b.name, 'ru');
      }),
    [clusters]
  );

  const presetsByKey = useMemo(
    () => Object.fromEntries(presets.map((preset) => [preset.key, preset])),
    [presets]
  );

  const filteredClusters = useMemo(() => {
    const query = clusterSearch.trim().toLowerCase();

    if (!query) {
      return sortedClusters;
    }

    return sortedClusters.filter((cluster) => {
      const serviceNames = (servicesByCluster[cluster.id] || [])
        .map((member) => member.service?.name || '')
        .join(' ');

      const haystack = [
        cluster.name,
        cluster.description || '',
        serviceNames,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [clusterSearch, servicesByCluster, sortedClusters]);

  const visibleClusters = useMemo(
    () => filteredClusters.slice(clusterOffset, clusterOffset + CLUSTERS_PAGE_SIZE),
    [clusterOffset, filteredClusters]
  );

  useEffect(() => {
    setClusterOffset(0);
  }, [clusterSearch]);

  useEffect(() => {
    if (!selectedClusterId) {
      return;
    }

    const selectedIndex = filteredClusters.findIndex((cluster) => cluster.id === selectedClusterId);
    if (selectedIndex >= 0) {
      setClusterOffset(Math.floor(selectedIndex / CLUSTERS_PAGE_SIZE) * CLUSTERS_PAGE_SIZE);
    }
  }, [filteredClusters, selectedClusterId]);

  useEffect(() => {
    if (expandedClusterId && !filteredClusters.some((cluster) => cluster.id === expandedClusterId)) {
      setExpandedClusterId(null);
    }
  }, [expandedClusterId, filteredClusters]);

  useEffect(() => {
    if (presets.length > 0 && selectedPresetKeys.length === 0) {
      setSelectedPresetKeys(presets.map((preset) => preset.key));
    }
  }, [presets, selectedPresetKeys.length]);

  const handleToggleExpand = (clusterId: string) => {
    setExpandedClusterId((prev) => (prev === clusterId ? null : clusterId));
  };

  const handleSelectCluster = (clusterId: string) => {
    const nextClusterId = selectedClusterId === clusterId ? null : clusterId;
    onSelectCluster?.(nextClusterId);
  };

  const resolveClusterOriginState = (cluster: ServiceCluster): 'canonical' | 'custom' | 'overridden' => {
    if (!cluster.is_registry_managed || !cluster.registry_key) {
      return 'custom';
    }

    const preset = presetsByKey[cluster.registry_key];
    if (!preset) {
      return 'overridden';
    }

    const sameDescription = (cluster.description || '') === (preset.description || '');
    const sameColor = (cluster.color || '') === (preset.color || '');
    const sameIcon = (cluster.icon || '') === (preset.icon || '');
    const sameOrder = cluster.display_order === preset.display_order;

    return sameDescription && sameColor && sameIcon && sameOrder ? 'canonical' : 'overridden';
  };

  const handlePresetToggle = (presetKey: string) => {
    setSelectedPresetKeys((prev) =>
      prev.includes(presetKey) ? prev.filter((key) => key !== presetKey) : [...prev, presetKey]
    );
  };

  const handlePreviewPresets = async () => {
    const data = await previewPresets.mutateAsync({ preset_keys: selectedPresetKeys });
    setPresetPreview(data);
  };

  const handleApplyPresets = async () => {
    const data = await applyPresets.mutateAsync({ preset_keys: selectedPresetKeys });
    setPresetPreview(data.preview);
  };

  return (
    <>
      <Card id="service-clusters-panel" className={className}>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <CardTitle>Кластеры услуг</CardTitle>
                <p className="text-sm text-neutral-600">
                  Выберите кластер, чтобы отфильтровать список услуг, и раскрывайте детали только там, где нужно.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Создать кластер
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsPresetsModalOpen(true)}
              >
                Канонические пресеты
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSyncClusters}
                disabled={syncClusters.isPending}
              >
                <RefreshCw className={`mr-1 h-4 w-4 ${syncClusters.isPending ? 'animate-spin' : ''}`} />
                {syncClusters.isPending ? 'Синхронизируем...' : 'Синк из Data Layer'}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{clusters.length} всего</Badge>
              <Badge variant="secondary">
                {clusters.filter((cluster) => cluster.is_active).length} активных
              </Badge>
              <Badge variant="secondary">
                {clusters.filter((cluster) => cluster.sync_origin === 'datalayer_manual' || cluster.sync_origin === 'datalayer_auto').length} из Data Layer
              </Badge>
              <Badge variant="secondary">
                {clusters.filter((cluster) => cluster.sync_origin === 'local_manual').length} с локальным override
              </Badge>
              {selectedClusterId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectCluster?.(null)}
                >
                  Сбросить фильтр кластера
                </Button>
              )}
            </div>

            <SearchInput
              value={clusterSearch}
              onChange={setClusterSearch}
              placeholder="Поиск по кластерам и услугам..."
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-neutral-500">
              Загрузка кластеров...
            </div>
          ) : loadError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-8 text-center">
              <p className="mb-2 font-medium text-amber-900">{loadError}</p>
              <p className="mb-4 text-sm text-amber-800">
                Часто это совпадает с фоновым синком из Data Layer. Подождите минуту и повторите.
              </p>
              {onRetry && (
                <Button variant="secondary" size="sm" onClick={onRetry} disabled={isRetrying}>
                  <RefreshCw className={`mr-1 h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Повтор...' : 'Повторить'}
                </Button>
              )}
            </div>
          ) : clusters.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <p className="mb-2">Нет кластеров</p>
              <p className="text-sm">Создайте кластер для группировки услуг</p>
            </div>
          ) : filteredClusters.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-neutral-500">
              <p className="mb-2 font-medium text-neutral-700">Ничего не найдено</p>
              <p className="text-sm">Попробуйте изменить запрос поиска по кластерам.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleClusters.map((cluster) => (
                <ClusterCard
                  key={cluster.id}
                  cluster={cluster}
                  services={servicesByCluster[cluster.id] || []}
                  draggedService={draggedService}
                  onDrop={onDrop}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                  onOpenServices={setServicesShellCluster}
                  isExpanded={expandedClusterId === cluster.id}
                  isSelected={selectedClusterId === cluster.id}
                  onToggleExpand={handleToggleExpand}
                  onSelect={handleSelectCluster}
                  originState={resolveClusterOriginState(cluster)}
                />
              ))}

              <Pagination
                offset={clusterOffset}
                limit={CLUSTERS_PAGE_SIZE}
                total={filteredClusters.length}
                onOffsetChange={setClusterOffset}
                showPageNumbers={false}
                className="pt-2"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ClusterServicesShell
        isOpen={!!servicesShellCluster}
        cluster={servicesShellCluster}
        services={servicesShellCluster ? servicesByCluster[servicesShellCluster.id] || [] : []}
        onClose={() => setServicesShellCluster(null)}
        onAddService={onDrop}
        onRemoveService={onRemoveService}
        onReorderServices={onReorderServices}
      />

      {/* Модальное окно создания кластера */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Создать кластер услуг"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!formData.name || createCluster.isPending}
              isLoading={createCluster.isPending}
            >
              Создать
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Название *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Например: Удаление зубов"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Описание
            </label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Описание кластера"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Цвет
            </label>
            <Select
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              options={DEFAULT_CLUSTER_COLORS.map(c => ({ value: c.value, label: c.label }))}
            />
            <div className="mt-2 w-full h-8 rounded border" style={{ backgroundColor: formData.color }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Иконка
            </label>
            <Select
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              options={CLUSTER_ICON_OPTIONS.map(i => ({ value: i.value, label: `${i.glyph} ${i.label}` }))}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-neutral-700">Активен</span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Модальное окно редактирования кластера */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCluster(null);
        }}
        title={`Редактировать кластер "${editingCluster?.name}"`}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingCluster(null);
              }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdate}
              disabled={!formData.name || updateCluster.isPending}
              isLoading={updateCluster.isPending}
            >
              Сохранить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Название *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Например: Удаление зубов"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Описание
            </label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Описание кластера"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Цвет
            </label>
            <Select
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              options={DEFAULT_CLUSTER_COLORS.map(c => ({ value: c.value, label: c.label }))}
            />
            <div className="mt-2 w-full h-8 rounded border" style={{ backgroundColor: formData.color }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Иконка
            </label>
            <Select
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              options={CLUSTER_ICON_OPTIONS.map(i => ({ value: i.value, label: `${i.glyph} ${i.label}` }))}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-neutral-700">Активен</span>
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPresetsModalOpen}
        onClose={() => setIsPresetsModalOpen(false)}
        title="Канонические кластеры услуг"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsPresetsModalOpen(false)}>
              Закрыть
            </Button>
            <Button
              variant="secondary"
              onClick={handlePreviewPresets}
              disabled={selectedPresetKeys.length === 0 || previewPresets.isPending}
              isLoading={previewPresets.isPending}
            >
              Предпросмотр
            </Button>
            <Button
              variant="primary"
              onClick={handleApplyPresets}
              disabled={selectedPresetKeys.length === 0 || applyPresets.isPending}
              isLoading={applyPresets.isPending}
            >
              Применить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
            Dry-run покажет, какие кластеры будут созданы и сколько услуг попадёт в каждый preset.
          </div>

          <div className="space-y-3">
            {presets.map((preset: ClusterPreset) => (
              <label
                key={preset.key}
                className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3"
              >
                <input
                  type="checkbox"
                  checked={selectedPresetKeys.includes(preset.key)}
                  onChange={() => handlePresetToggle(preset.key)}
                  className="mt-1 h-4 w-4"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-neutral-900">{preset.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {preset.key}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-neutral-600">{preset.description}</p>
                </div>
              </label>
            ))}
          </div>

          {presetPreview && (
            <div className="space-y-3 rounded-xl border border-neutral-200 p-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{presetPreview.matched_services_total} совпадений</Badge>
                <Badge variant="secondary">{presetPreview.will_create_clusters} новых кластеров</Badge>
                <Badge variant="secondary">{presetPreview.will_add_memberships} новых membership</Badge>
                <Badge variant="secondary">{presetPreview.overlap_services_total} overlap-услуг</Badge>
              </div>

              <div className="space-y-2">
                {presetPreview.presets.map((item) => (
                  <div key={item.preset.key} className="rounded-lg border border-neutral-200 p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-neutral-900">{item.preset.name}</span>
                      {item.will_create_cluster ? (
                        <Badge className="bg-emerald-50 text-emerald-700 text-xs">Будет создан</Badge>
                      ) : (
                        <Badge className="bg-sky-50 text-sky-700 text-xs">Будет обновлён</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-600">
                      <span>совпадений: {item.matched_count}</span>
                      <span>добавится: {item.will_add_count}</span>
                      <span>overlap: {item.overlap_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
